import { useEffect, useRef } from 'react'
import axios from 'axios'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './store/authStore'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import VerifyEmailPage from './pages/VerifyEmailPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import AgreementPage from './pages/AgreementPage'
import DashboardPage from './pages/DashboardPage'
import QuestionnairePage from './pages/QuestionnairePage'
import RiskApprovalPage from './pages/RiskApprovalPage'
import ChatPage from './pages/ChatPage'
import StudentLoginPage from './pages/StudentLoginPage'
import StudentSignupPage from './pages/StudentSignupPage'
import StudentChatPage from './pages/StudentChatPage'
import StudentHubPage from './pages/StudentHubPage'
import StudentTopicPage from './pages/StudentTopicPage'
import StudentExamPage from './pages/StudentExamPage'
import ProfilePage from './pages/ProfilePage'
import AdminPage from './pages/AdminPage'
import SubscriptionPage from './pages/SubscriptionPage'
import PaymentResultPage from './pages/PaymentResultPage'
import AccessibilityStatementPage from './pages/AccessibilityStatementPage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import TermsOfUsePage from './pages/TermsOfUsePage'
import CookiePolicyPage from './pages/CookiePolicyPage'
import InstallPage from './pages/InstallPage'
import Layout from './components/layout/Layout'
import PublicLayout from './components/layout/PublicLayout'
import ProtectedRoute from './components/auth/ProtectedRoute'
import LandingPage from './pages/LandingPage'
import TrackRoot from './pages/track/TrackRoot'
import MazanRoot from './pages/mazan/MazanRoot'
import { TRACKS } from './config/tracks'

/**
 * Decode JWT payload without verifying signature (client-side exp check only)
 */
function getTokenExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp ?? null
  } catch {
    return null
  }
}

function App() {
  const checkingRef = useRef(false)

  // Read fresh state from store every time (not from closure).
  // Instead of logging the user out when the access token lapses, we silently
  // renew it in the background so the user is never signed out for inactivity.
  // Logout only happens if the refresh genuinely fails (refresh token revoked/expired).
  const checkSession = async (source?: string) => {
    console.log(`[session-check] triggered by: ${source || 'unknown'}`)

    if (checkingRef.current) return
    checkingRef.current = true

    try {
      const { token, isAuthenticated, logout, setToken } = useAuthStore.getState()

      if (!isAuthenticated || !token) return

      const exp = getTokenExp(token)
      if (!exp) return

      const remaining = Math.round((exp * 1000 - Date.now()) / 1000)
      console.log(`[session-check] token expires in ${remaining}s`)

      // Renew slightly before expiry (within 120s) so the session never actually lapses
      if (remaining > 120) return

      // Token expired or about to — silently renew via refresh token cookie
      console.log('[session-check] token near/at expiry → refreshing')
      try {
        const response = await axios.post(
          '/api/v1/auth/refresh',
          {},
          { withCredentials: true }
        )
        const accessToken = response.data?.tokens?.accessToken
        if (accessToken) {
          setToken(accessToken)
          console.log('[session-check] token refreshed')
        }
      } catch {
        // Refresh failed — session genuinely invalid (not idle). Now log out.
        console.log('[session-check] refresh FAILED → logging out')
        logout()
        window.location.href = '/login?expired=1'
      }
    } finally {
      checkingRef.current = false
    }
  }

  useEffect(() => {
    const onVisibility = () => {
      console.log(`[session-check] visibilitychange → ${document.visibilityState}`)
      if (document.visibilityState === 'visible') checkSession('visibilitychange')
    }
    const onFocus = () => checkSession('focus')

    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onFocus)

    // Heartbeat: check every 30s as fallback
    const interval = setInterval(() => checkSession('heartbeat'), 30_000)

    console.log('[session-check] listeners attached: visibilitychange, focus, heartbeat(30s)')

    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', onFocus)
      clearInterval(interval)
    }
  }, [])

  // Also check once on mount
  useEffect(() => {
    checkSession()
  }, [])

  return (
    <Routes>
      {/* Public front door — the four areas of the app. Shown to everyone,
          signed in or not, so the areas are discoverable from one place. */}
      <Route element={<PublicLayout withFooter />}>
        <Route path="/" element={<LandingPage />} />
      </Route>

      {/* Public routes — wrapped with skip link + <main> landmark */}
      <Route element={<PublicLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/student-login" element={<StudentLoginPage />} />
        <Route path="/register-student" element={<StudentSignupPage />} />
        <Route path="/register-student/:code" element={<StudentSignupPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/agreement" element={<AgreementPage />} />
        <Route path="/risk-approval" element={<RiskApprovalPage />} />
        <Route path="/accessibility" element={<AccessibilityStatementPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfUsePage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />
        {/* Shareable install guide — sent by WhatsApp/SMS, must work logged out */}
        <Route path="/install" element={<InstallPage />} />
      </Route>

      {/* Protected routes */}
      <Route element={<Layout />}>
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/questionnaire"
          element={
            <ProtectedRoute>
              <QuestionnairePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat"
          element={
            <ProtectedRoute requireQuestionnaire>
              <ChatPage />
            </ProtectedRoute>
          }
        />
        {/* Student learning path — hub of topic tiles, lesson+chat, final exam.
            ProtectedRoute already allows any path starting with /learn for students. */}
        <Route
          path="/learn"
          element={
            <ProtectedRoute requireRole={['student']}>
              <StudentHubPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/learn/topic/:slug"
          element={
            <ProtectedRoute requireRole={['student']}>
              <StudentTopicPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/learn/exam"
          element={
            <ProtectedRoute requireRole={['student']}>
              <StudentExamPage />
            </ProtectedRoute>
          }
        />
        {/* The original free-form educational chat, kept for questions that don't
            belong to any one topic. */}
        <Route
          path="/learn/chat"
          element={
            <ProtectedRoute requireRole={['student']}>
              <StudentChatPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscription"
          element={
            <ProtectedRoute>
              <SubscriptionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment/result"
          element={
            <ProtectedRoute>
              <PaymentResultPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            // Section admins (sub_admin) reach the panel too — it renders only the
            // areas delegated to them, and the API refuses everything else.
            <ProtectedRoute requireRole={['admin', 'advisor', 'sub_admin']}>
              <AdminPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Portal tracks. Each owns its whole subtree (entry page when signed out,
          hub/lesson/chat when signed in) — see TrackRoot. */}
      <Route path={`${TRACKS.retirement.basePath}/*`} element={<TrackRoot track="retirement" />} />
      <Route path={`${TRACKS.mortgage.basePath}/*`} element={<TrackRoot track="mortgage" />} />

      {/* מאזן — the one-time-paid איזון משאבים report (a separate service).
          react-router matches case-insensitively, so /mazan works too. */}
      <Route path="/Mazan/*" element={<MazanRoot />} />

      {/* English aliases, so both spellings of the URL work. */}
      <Route path="/retirement/*" element={<Navigate to={TRACKS.retirement.basePath} replace />} />
      <Route path="/mortgage/*" element={<Navigate to={TRACKS.mortgage.basePath} replace />} />

      {/* Unknown path — send people to the front door rather than a dead end. */}
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  )
}

/**
 * Where an unknown URL lands. Students go to their portal (they are confined to
 * it anyway); everyone else gets the public landing page, which now works signed
 * in as well as signed out.
 */
function HomeRedirect() {
  const { user } = useAuthStore()
  return <Navigate to={user?.role === 'student' ? '/learn' : '/'} replace />
}

export default App
